import { ReactElement, useEffect, useState } from "react";
import { ReactSVG } from "react-svg";
import { ColumnsType } from "antd/es/table";

import "./users-list.scss";
import LockImage from "../../../../assets/lock.svg";
import { Tooltip } from "antd";
import CustomTable from "../../../../components/custom-table/custom-table";
import CustomModal from "../../../../components/custom-modal/custom-modal";
import AddEditUser from "../add-edit-user/add-edit-user";
import { Link } from "react-router-dom";
import ViewUser from "../view-user/view-user";
import { useDispatch, useSelector } from "react-redux";
import { LOADING } from "../../../../store/actions";
import instance from "../../../../store/axios";
import { AxiosResponse } from "axios";
import LockModal from "../../../../components/lock-modal/lock-modal";
import toast from "react-hot-toast";
import { UnlockOutlined } from "@ant-design/icons";
import clsx from "clsx";
import {
  ApprovalGroups,
  WithdrawalGroups,
} from "../../../../utils/data/constant";
import moment from "moment";
import DeleteModal from "src/components/delete-modal/delete-modal";
import BackendUserService from "./backend-user.service";

const UsersList: React.FC = (): ReactElement => {
  const dispatch = useDispatch();
  const loadingState = useSelector((state: any) => state?.loading);
  const backendUserServiceInstance = new BackendUserService();
  const [usersData, setUsersData] = useState<any>([]);
  const [selectedUser, setSelectedUser] = useState<any>();
  const [openEditUserModal, setOpenEditUserModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [openLockModal, setOpenLockModal] = useState<boolean>(false);
  const [openSendResetLink, setOpenSendResetLink] = useState<boolean>(false);
  const [openForceUpdatePassword, setOpenForceUpdatePassword] =
    useState<boolean>(false);

  useEffect(() => {
    getUsersData();
  }, []);

  const getUsersData = async (): Promise<void> => {
    dispatch({ type: LOADING, payload: true });

    try {
      const response: AxiosResponse<any> = await instance.get(`/${process.env.REACT_APP_ENDPOINT_VERSION}/admin`);
      setUsersData(response.data);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem("star_prev_url", `/backend-users`);
    }

    dispatch({ type: LOADING, payload: false });
  };

  const sendResetLink = async () => {
    const response = await backendUserServiceInstance?.sendResetPasswordEmail(
      selectedUser?.id,
      selectedUser?.email
    );
    if (response) {
      setOpenSendResetLink(false);
    }
  };
  
  const columns: ColumnsType<any> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Full name",
      dataIndex: "name",
      key: "name",
      width: 200,
      sorter: (a, b) => String(a?.name).localeCompare(String(b?.name)),
      fixed: "left",
      render: (text, record) => (
        <Link
          to=""
          onClick={() => {
            setSelectedUser(record);
            setOpenViewModal(true);
          }}
        >
          {text}
        </Link>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => String(a?.email).localeCompare(String(b?.email)),
    },
    {
      title: "Branch",
      dataIndex: "branch",
      key: "branch",
      filters: [
        {
          text: "Kuwait",
          value: "KW",
        },
        {
          text: "Suadi",
          value: "SA",
        },
        {
          text: "UAE",
          value: "AE",
        },
      ],
      onFilter: (value, record) =>
        record.adminBranch?.some((branch: any) => branch.branch === value),
      render: (_, record: any) => (
        <span>
          {record?.adminBranch
            ?.map((branch: any) => branch.branch)
            ?.join(", ") || "-"}
        </span>
      ),
    },
    {
      title: "KYC review group",
      dataIndex: "kycReviewGroup",
      key: "kycReviewGroup",
      sorter: (a, b) =>
        String(ApprovalGroups[a.kycReviewGroup]).localeCompare(
          String(ApprovalGroups[b.kycReviewGroup])
        ),
      render: (text: string) => <span>{ApprovalGroups[text] || "-"}</span>,
    },
    {
      title: "Withdraw group",
      dataIndex: "withdrawGroup",
      key: "withdrawGroup",
      sorter: (a, b) =>
        String(WithdrawalGroups[a.withdrawGroup]).localeCompare(
          String(WithdrawalGroups[b.withdrawGroup])
        ),
      render: (text: string) => <span>{WithdrawalGroups[text] || "-"}</span>,
    },
    {
      title: "Roles",
      dataIndex: "roles",
      key: "roles",
      render: (_, record) => (
        <span>{record.roles?.map((role: any) => role.role).join(", ")}</span>
      ),
    },
    {
      title: "Last login",
      dataIndex: "lastLoginAt",
      key: "lastLoginAt",
      width: 190,
      sorter: (a, b) => {
        if (!a.lastLoginAt && !b.lastLoginAt) return 0;
        if (!a.lastLoginAt) return 1;
        if (!b.lastLoginAt) return -1;

        return moment(a.lastLoginAt).isBefore(moment(b.lastLoginAt)) ? -1 : 1;
      },
      render: (text) => (
        <span className="flex justify-center text-gray-500">
          {text ? moment(text).format("DD MMM, YYYY hh:mm A") : "-"}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      sorter: (a, b) => String(a?.isActive).localeCompare(String(b?.isActive)),
      filters: [
        {
          text: "Active",
          value: true,
        },
        {
          text: "Inactive",
          value: false,
        },
      ],
      onFilter: (value, record) => record.isActive === value,
      render: (text) => (
        <span
          className={clsx(
            "rounded-md text-sm w-[80px] flex items-center justify-center h-[35px]",
            !text ? "bg-red-200 text-white" : "bg-gray-600 text-green-500"
          )}
        >
          {text ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <div className="actions flex align-center">
          <ReactSVG
            src={"/images/edit.svg"}
            onClick={() => {
              setSelectedUser(record);
              setOpenEditUserModal(true);
            }}
          />

          {record.isActive ? (
            <ReactSVG
              src={LockImage}
              className="lock-icon"
              onClick={() => {
                setSelectedUser(record);
                setOpenLockModal(true);
              }}
            />
          ) : (
            <UnlockOutlined
              src={LockImage}
              className="lock-icon"
              onClick={() => {
                setSelectedUser(record);
                setOpenLockModal(true);
              }}
            />
          )}

          <Tooltip title="Send Reset Link">
            <img
              src="/images/reset-link-icon.svg"
              onClick={() => {
                setSelectedUser(record);
                setOpenSendResetLink(true);
              }}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const lockUser = async () => {
    dispatch({ type: LOADING, payload: true });

    try {
      await instance.post(`/${process.env.REACT_APP_ENDPOINT_VERSION}/admin/${selectedUser?.id}`, {
        isActive: !selectedUser.isActive,
      });
      toast.success("User updated successfully");
      getUsersData();
      setOpenLockModal(false);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem("star_prev_url", `/backend-users`);
    }
    dispatch({ type: LOADING, payload: false });
  };

  return (
    <div className="users-table">
      <div className="title flex align-center justify-space-between">
        <p>{!loadingState ? usersData?.length : ""} Users</p>
        <div className="images flex align-center">
          {/* <CustomSearch />
          <Tooltip placement="topLeft" title="Columns settings">
            <img src={"/images/grid.svg"} alt="grid" />
          </Tooltip>
          <Tooltip placement="topLeft" title="Export">
            <img src={"/images/export.svg"} alt="export-customers" />
          </Tooltip>
          <Tooltip placement="topLeft" title="Upload">
            <img src={"/images/upload.svg"} alt="upload-customers" />
          </Tooltip> */}
        </div>
      </div>
      {usersData?.length ? (
        <CustomTable
          columns={columns}
          data={usersData}
          showPagination={false}
        />
      ) : !loadingState ? (
        <div className="flex flex-col gap-3 w-full items-center justify-center mt-9">
          <img
            className="w-[40px] h-[40px]"
            src="/images/no-items.svg"
            alt="no data kamco"
          />
          <p className="text-lg text-gray-500 font-medium">
            There are no items in this list
          </p>
        </div>
      ) : (
        ""
      )}
      <CustomModal
        isVisible={openEditUserModal}
        centered
        width={900}
        showClose={false}
        onClose={() => {
          setOpenEditUserModal(false);
        }}
        className="edit-modal"
      >
        <div className="flex column align-center justify-center">
          <AddEditUser
            selectedUser={selectedUser}
            edit={true}
            onCancel={() => {
              setOpenEditUserModal(false);
            }}
            onConfirm={() => {
              getUsersData();
              setOpenEditUserModal(false);
            }}
          />
        </div>
      </CustomModal>
      <CustomModal
        isVisible={openViewModal}
        centered
        width={900}
        showClose={false}
        onClose={() => {
          setOpenViewModal(false);
        }}
        className="edit-modal"
      >
        <div className="flex column align-center justify-center">
          <ViewUser
            selectedUser={selectedUser}
            onCancel={() => {
              setOpenViewModal(false);
            }}
            onEdit={() => {
              setOpenViewModal(false);
              setOpenEditUserModal(true);
            }}
          />
        </div>
      </CustomModal>

      <CustomModal
        isVisible={openLockModal}
        centered
        width={450}
        onClose={() => {
          setOpenLockModal(false);
        }}
        className="delete-modal"
      >
        <LockModal
          onCancel={() => setOpenLockModal(false)}
          onConfirm={lockUser}
          text={selectedUser?.isActive ? "Lock user!" : "unlock user!"}
          subText={`Are you sure you want to ${
            selectedUser?.isActive ? "Lock" : "unlock"
          } this user “${selectedUser?.name}”?`}
        />
      </CustomModal>

      <CustomModal
        isVisible={openSendResetLink}
        centered
        width={450}
        onClose={() => {
          setOpenSendResetLink(false);
        }}
        className="delete-modal"
      >
        <DeleteModal
          icon="/images/confidential-email.svg"
          onCancel={() => setOpenSendResetLink(false)}
          onConfirm={sendResetLink}
          text="Reset password link!"
          subText={`Are you sure you want to send reset password link?`}
        />
      </CustomModal>
    </div>
  );
};

export default UsersList;
